import React, { useState, useEffect, useMemo, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";

// import CommonTooltip from "../../components/common/CommonToolTip";

import {
  extractFirstJsonObject,
  extractUidAndMessage,
  fixMalformedJsonString,
  isLikelyUid,
} from "../../common/Constants";
import ChatLoadingIndicator, { DeepseekLoader } from "../../common/Loader";
import DocumentList from "./DocumentList";
import useCommonStore from "../../store/CommonStore";

// import { addToast } from "../lib/useToastContext";

interface LlmResponseFormatProps {
  generatedText: string;
  llm: string;
  okrs: any;
  promptsWithMessages?: any;
}

type MarkdownCodeProps = {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
} & React.HTMLAttributes<HTMLElement>;

const containsDetails = (text: string) =>
  /<details>.*?<\/details>/gs.test(text);

const parseDetails = (
  content: string
): {
  parsed: { title: string; content: string }[];
  plainText: string;
} => {
  const detailsRegex = /<details>(.*?)<\/details>/gs;
  const summaryRegex = /<summary>(.*?)<\/summary>/s;

  const matches = [...content.matchAll(detailsRegex)];
  const parsed = matches.map((match, idx) => {
    const summaryMatch = summaryRegex.exec(match[1]);
    const summary = summaryMatch?.[1] ?? `Category ${idx + 1}`;
    const body = match[1].replace(summaryRegex, "").trim();
    return { title: summary.replace(/<\/?strong>/g, ""), content: body };
  });

  const plainText = content.replace(detailsRegex, "").trim();
  return { parsed, plainText };
};

const LlmResponseFormat: React.FC<LlmResponseFormatProps> = ({
  generatedText,
  llm,
  okrs,
}) => {
  const { streaming, thinkTime } = useCommonStore();

  const processedContent = useMemo(() => {
    return isLikelyUid(generatedText)
      ? extractUidAndMessage(generatedText).message
      : generatedText;
  }, [generatedText]);

  const isDeepseek = llm === "Deepseek";
  const displayTime = useMemo(
    () => ((thinkTime || okrs) / 1000).toFixed(2),
    [thinkTime, okrs]
  );

  const [currentStreamingIndex, setCurrentStreamingIndex] = useState(0);
  const [isStreaming, setIsStreaming] = useState(true);
  const [isThoughtExpanded, setIsThoughtExpanded] = useState(true);

  const { parsed, plainText } = useMemo(
    () => parseDetails(processedContent),
    [processedContent]
  );

  const { json: rawJson, rest: markdownRest } = useMemo(
    () => extractFirstJsonObject(plainText),
    [plainText]
  );

  const formattedJson = useMemo(() => {
    if (!rawJson) return null;
    try {
      const fixed = fixMalformedJsonString(rawJson);
      return JSON.parse(fixed);
    } catch {
      return null;
    }
  }, [rawJson]);

  const [thoughtContent, normalContent] = useMemo(() => {
    const [thoughtRaw = "", normalRaw = ""] =
      processedContent.split("</think>");
    const thought = thoughtRaw.replace(/<think>/g, "").trim();
    const cleanedThought = thought.replace(/\n{3,}/g, "\n\n").trim();
    const cleanedNormal = normalRaw.replace(/\n{3,}/g, "\n\n").trim();
    return [cleanedThought, cleanedNormal];
  }, [processedContent]);

  useEffect(() => {
    if (!isStreaming || currentStreamingIndex >= parsed.length) return;

    const timeout = setTimeout(() => {
      setCurrentStreamingIndex((prev) => prev + 1);
      if (currentStreamingIndex + 1 >= parsed.length) setIsStreaming(false);
    }, 500);

    return () => clearTimeout(timeout);
  }, [isStreaming, currentStreamingIndex, parsed.length]);

  const handleCopyCode = useCallback((code: string) => {
    navigator.clipboard.writeText(code);
    // addToast("success", "Copied successfully!");
  }, []);

  const renderMarkdown = useCallback(
    (content: string) => (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ href, children, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 underline hover:text-blue-700"
              {...props}
            >
              {children}
            </a>
          ),
          // code: ({
          //   inline,
          //   className,
          //   children,
          //   ...props
          // }: MarkdownCodeProps) => {
          //   const match = /language-(\w+)/.exec(className || "");
          //   const code = String(children).replace(/\n$/, "");
          //   if (!code || code === "undefined") return null;

          //   return !inline && match ? (
          //     <div className="relative rounded-md bg-gray-800 text-gray-200">
          //       <SyntaxHighlighter
          //         language={match[1]}
          //         PreTag="div"
          //         {...props}
          //         className="p-4 pr-12"
          //       >
          //         {code}
          //       </SyntaxHighlighter>
          //       <button
          //         onClick={() => handleCopyCode(code)}
          //         className="absolute top-2 right-2 bg-white text-black p-1 rounded hover:bg-gray-100"
          //       >
          //         <img src="/icons/copy.svg" alt="Copy" />
          //       </button>
          //       <CommonTooltip
          //         id="copy-code-tooltip"
          //         anchorSelect="#copy-code-btn"
          //         content="Copy"
          //         className="z-[100]"
          //       />
          //     </div>
          //   ) : (
          //     <code
          //       className="bg-gray-100 text-primary_1 p-1 rounded"
          //       {...props}
          //     >
          //       {children}
          //     </code>
          //   );
          // },

          code: ({
            inline,
            className,
            children,
            ...props
          }: MarkdownCodeProps) => {
            const match = /language-(\w+)/.exec(className || "");
            const code = String(children).replace(/\n$/, "");
            if (!code || code === "undefined") return null;

            const { style, ...filteredProps } = props;

            return !inline && match ? (
              <div className="relative rounded-md bg-gray-800 text-gray-200">
                <SyntaxHighlighter
                  language={match[1]}
                  PreTag="div"
                  {...filteredProps}
                  className="p-4 pr-12 overflow-visible" // 👈 ensure no overflow constraint
                  wrapLongLines // 👈 ensures long lines don't cause horizontal scroll
                >
                  {code}
                </SyntaxHighlighter>
                <button
                  onClick={() => handleCopyCode(code)}
                  className="absolute top-2 right-2 bg-white text-black p-1 rounded hover:bg-gray-100"
                >
                  <img src="/icons/copy.svg" alt="Copy" />
                </button>
              </div>
            ) : (
              <code
                className="bg-gray-100 text-primary_1 p-1 rounded"
                {...props}
              >
                {children}
              </code>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    ),
    [handleCopyCode]
  );

  return (
    <div className="content-list">
      {isDeepseek && (
        <details className="mb-3" open={isThoughtExpanded}>
          <summary
            onClick={(e) => {
              e.preventDefault();
              setIsThoughtExpanded((prev) => !prev);
            }}
            className="flex justify-between items-center p-3 bg-gray-100 rounded-lg cursor-pointer"
          >
            <span>
              {processedContent.includes("</think>")
                ? displayTime === "0.00"
                  ? "Thought completed"
                  : `Thought for ${displayTime} seconds`
                : "Thinking..."}
            </span>
            <img
              src="/icons/chevron-down.svg"
              alt="Toggle"
              className={`h-5 w-5 transform transition-transform ${
                isThoughtExpanded ? "rotate-180" : ""
              }`}
            />
          </summary>
          <div className="p-3 text-primary_1 border-l border-gray-300">
            {renderMarkdown(thoughtContent)}
            {streaming && !processedContent.includes("</think>") && (
              <DeepseekLoader />
            )}
          </div>
        </details>
      )}

      {containsDetails(processedContent) ? (
        parsed.map((detail, i) => (
          <div key={i}>
            {i === currentStreamingIndex && isStreaming ? (
              <div className="flex gap-3 items-center animate-pulse">
                <div className="h-6 w-6 bg-gray-200 rounded-full"></div>
                <div className="h-6 w-48 bg-gray-200 rounded-md"></div>
              </div>
            ) : i <= currentStreamingIndex ? (
              <>
                <details className="group cursor-pointer">
                  <summary className="flex justify-between items-center font-medium text-base">
                    <span>{detail.title}</span>
                    <img
                      src="/icons/chevron-down.svg"
                      className="h-6 w-6 group-open:rotate-180 transition-transform"
                      alt="Toggle"
                    />
                  </summary>
                  <div className="mt-3">{renderMarkdown(detail.content)}</div>
                </details>
                {i < parsed.length - 1 && (
                  <hr className="my-4 border-[#E0E0E0]" />
                )}
              </>
            ) : null}
          </div>
        ))
      ) : (
        <div className="mb-4">
          {/* {formattedJson && <DocumentList data={formattedJson} />}
           */}
          {formattedJson &&
            typeof formattedJson === "object" &&
            !Array.isArray(formattedJson) && (
              <DocumentList data={formattedJson} />
            )}
          {plainText.trim() === "Generating response, please wait..." ? (
            <ChatLoadingIndicator />
          ) : (
            renderMarkdown(isDeepseek ? normalContent : markdownRest)
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(LlmResponseFormat);
